import { createSlice } from '@reduxjs/toolkit'

import glossaryAutomationService, { FLAG_FILTER_KEYS } from '../services/glossaryAutomation'
import { setSuccessNotification, setErrorNotification } from './notificationReducer'

const initialState = {
    // DRF pagination envelope, flattened. resource_hub.pagination.StandardResultsSetPagination
    // sends { count, next, previous, total_pages, current_page, results }, so the page
    // count comes off the wire — no client-side arithmetic over a duplicated page size.
    flags: [],
    count: 0,
    next: null,
    previous: null,
    totalPages: 1,
    // The query that produced the current list — kept so a resolve/dismiss knows
    // whether the updated row still belongs in it.
    page: 1,
    filters: {},
    loading: false,
    scanning: false,
    // { enabled, max_age_years, link_check_enabled }; null until the first fetch resolves.
    settings: null,
    // The last scan summary ({ ran, created, resolved, suppressed, skipped_checks }).
    lastScan: null,
    // Latest failure message, or null. Notifications are transient; this is what a
    // panel can render inline.
    error: null,
}

// Keep only the whitelisted, non-empty filter params — the same set the service puts
// on the query string, so state.filters always describes the request that was made.
const pickFilters = (params = {}) => {
    const filters = {}
    for (const key of FLAG_FILTER_KEYS) {
        if (params[key]) filters[key] = params[key]
    }
    return filters
}

const glossaryAutomationSlice = createSlice({
    name: 'glossaryAutomation',
    initialState,
    reducers: {
        setFlags(state, action) {
            const {
                count = 0, next = null, previous = null, results = [], total_pages: totalPages = null,
            } = action.payload || {}
            state.flags = results
            state.count = count
            state.next = next
            state.previous = previous
            // A response without total_pages (a stubbed or older envelope) is treated as a
            // single page rather than guessed at from count — the server owns the page size.
            state.totalPages = totalPages || 1
        },
        setQuery(state, action) {
            state.page = action.payload.page
            state.filters = action.payload.filters
        },
        // Fold a server-returned flag back into the list so an action doesn't need a
        // refetch. When the list is filtered by status and the flag has left that
        // status, drop the row instead of leaving a mismatched one on screen.
        updateFlag(state, action) {
            const flag = action.payload
            const index = state.flags.findIndex(f => f.id === flag.id)
            if (index === -1) return
            if (state.filters.status && flag.status !== state.filters.status) {
                state.flags.splice(index, 1)
                state.count = Math.max(0, state.count - 1)
            } else {
                state.flags[index] = flag
            }
        },
        setLoading(state, action) {
            state.loading = action.payload
        },
        setScanning(state, action) {
            state.scanning = action.payload
        },
        setSettings(state, action) {
            state.settings = action.payload
        },
        setScanResult(state, action) {
            state.lastScan = action.payload
        },
        setError(state, action) {
            state.error = action.payload
        },
    },
})

export const {
    setFlags,
    setQuery,
    updateFlag,
    setLoading,
    setScanning,
    setSettings,
    setScanResult,
    setError,
} = glossaryAutomationSlice.actions

// Fetch a page of flags. params: { page, status, kind }.
export const fetchFlags = (params = {}) => {
    return async dispatch => {
        dispatch(setLoading(true))
        dispatch(setError(null))
        try {
            const data = await glossaryAutomationService.getFlags(params)
            dispatch(setQuery({ page: params.page || 1, filters: pickFilters(params) }))
            dispatch(setFlags(data))
            return data
        } catch {
            const message = 'Error retrieving glossary automation flags'
            dispatch(setError(message))
            dispatch(setErrorNotification(message))
            return null
        } finally {
            dispatch(setLoading(false))
        }
    }
}

// Run a scan pass. Returns the summary so the caller can decide whether to refetch the
// list (a scan can create/resolve any number of rows, so in-place surgery isn't enough).
// ran === false means automation is switched off — that is not a success.
export const runScan = () => {
    return async dispatch => {
        dispatch(setScanning(true))
        dispatch(setError(null))
        try {
            const result = await glossaryAutomationService.runScan()
            dispatch(setScanResult(result))
            if (result && result.ran === false) {
                const message = 'Glossary automation is disabled, so nothing was scanned'
                dispatch(setError(message))
                dispatch(setErrorNotification(message))
            } else {
                const { created = 0, resolved = 0, skipped_checks: skipped = [] } = result || {}
                // A skipped check means part of the scan didn't run (e.g. link checking
                // switched off), so it rides on the summary rather than being silently
                // dropped into lastScan for the UI to remember to surface.
                const skippedNote = skipped.length ? ` (skipped: ${skipped.join(', ')})` : ''
                dispatch(setSuccessNotification(
                    `Scan complete: ${created} new flag(s), ${resolved} resolved${skippedNote}`))
            }
            return result
        } catch {
            const message = 'Error running the glossary automation scan'
            dispatch(setError(message))
            dispatch(setErrorNotification(message))
            return null
        } finally {
            dispatch(setScanning(false))
        }
    }
}

// Both flag actions return the updated flag; fold it straight into the list.
const flagAction = (id, serviceCall, successMessage, errorMessage) => {
    return async dispatch => {
        dispatch(setError(null))
        try {
            const flag = await serviceCall(id)
            dispatch(updateFlag(flag))
            dispatch(setSuccessNotification(successMessage))
            return flag
        } catch {
            dispatch(setError(errorMessage))
            dispatch(setErrorNotification(errorMessage))
            return null
        }
    }
}

export const resolveFlag = (id) =>
    flagAction(id, glossaryAutomationService.resolveFlag, 'Flag resolved', 'Error resolving flag')

export const dismissFlag = (id) =>
    flagAction(id, glossaryAutomationService.dismissFlag, 'Flag dismissed', 'Error dismissing flag')

export const fetchSettings = () => {
    return async dispatch => {
        try {
            const data = await glossaryAutomationService.getSettings()
            dispatch(setSettings(data))
            return data
        } catch {
            const message = 'Error retrieving glossary automation settings'
            dispatch(setError(message))
            dispatch(setErrorNotification(message))
            return null
        }
    }
}

// Save the settings form. Returns true/false so the form can report success. On a 400,
// surfaces the backend's per-field detail — "error saving" alone doesn't tell the admin
// what to fix.
export const saveSettings = (payload) => {
    return async dispatch => {
        dispatch(setError(null))
        try {
            const data = await glossaryAutomationService.updateSettings(payload)
            dispatch(setSettings(data))
            dispatch(setSuccessNotification('Glossary automation settings saved'))
            return true
        } catch (err) {
            const data = err?.response?.data
            const detail = data?.detail || (data && typeof data === 'object'
                ? Object.entries(data).map(([field, msgs]) => `${field}: ${[].concat(msgs).join(' ')}`).join('; ')
                : null)
            const message = detail || 'Error saving glossary automation settings'
            dispatch(setError(message))
            dispatch(setErrorNotification(message))
            return false
        }
    }
}

export default glossaryAutomationSlice.reducer
